<template>
  <div class="min-h-screen bg-gray-50 p-3">
    <div class="max-w-6xl mx-auto">
      <!-- Page Header -->
      <div class="mb-4">
        <div class="relative rounded-2xl p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden w-full" 
             style="background: linear-gradient(135deg, #059669 0%, #047857 25%, #065F46 50%, #064E3B 75%, #022C22 100%);">
          
          <!-- Background decorative elements -->
          <div class="absolute inset-0 overflow-hidden">
            <div class="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-20"
                 style="background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);"></div>
          </div>
          
          <div class="relative z-10">
            <div class="flex items-center space-x-4 mb-3">
              <div class="relative flex-shrink-0">
                <div class="w-16 h-16 rounded-full flex items-center justify-center border-3 border-white/70 shadow-xl" 
                     style="background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);">
                  <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              
              <div>
                <h1 class="text-2xl font-bold text-white">{{ directT('settings.dealsSettings.title') }}</h1>
                <p class="text-white/90 text-lg font-medium">{{ directT('settings.dealsSettings.subtitle') }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="mt-4">
          <SettingsNav />
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <!-- Customer Status Settings -->
        <div class="bg-white rounded-2xl border border-gray-200 p-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center" style="background-color: #1E40AF;">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900">{{ directT('settings.dealsSettings.statusManagement.title') }}</h3>
            </div>
          </div>
          
          <div class="mb-4">
            <div v-if="statusList.length === 0" class="text-center py-8">
              <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <h4 class="text-sm font-semibold text-gray-900 mb-1">{{ directT('settings.dealsSettings.statusManagement.noStatusesTitle') }}</h4>
              <p class="text-gray-500 text-xs">{{ directT('settings.dealsSettings.statusManagement.noStatusesDescription') }}</p>
            </div>
            
            <!-- Status List -->
            <div v-else class="space-y-2">
              <div 
                v-for="(status, index) in statusList" 
                :key="status.id"
                class="group bg-gray-50 rounded-lg border border-gray-200 p-3 hover:bg-gray-100 transition-all duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: status.color }"></div>
                    <span class="text-sm font-medium text-gray-900">{{ status.name }}</span>
                  </div>
                  <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button 
                      @click="editStatus(index)" 
                      class="w-8 h-8 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-all duration-200 flex items-center justify-center"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    <button 
                      @click="confirmDeleteStatus(index)" 
                      class="w-8 h-8 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-all duration-200 flex items-center justify-center"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="flex gap-2 pt-4 border-t border-gray-200">
            <button 
              @click="addNewStatus" 
              class="flex-1 px-4 py-2 text-white rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              style="background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              {{ directT('settings.dealsSettings.statusManagement.addStatus') }}
            </button>
            
            <!-- Auto-save status indicator -->
            <div v-if="saving" class="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm">
              <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ directT('settings.dealsSettings.autoSaving.saving') }}
            </div>
          </div>
        </div>
        
        <!-- Customer Form Designer -->
        <div class="bg-white rounded-2xl border border-gray-200 p-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <!-- Existing Cards -->
          <div class="mb-6">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg flex items-center justify-center" style="background: linear-gradient(135deg, #3B82F6, #2563EB);">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-gray-900">{{ directT('settings.dealsSettings.existingCards.title') }}</h4>
                  <p class="text-xs text-gray-600">{{ directT('settings.dealsSettings.existingCards.description') }}</p>
                </div>
              </div>
              <button 
                @click="addNewCard" 
                class="px-4 py-2 text-white text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105"
                style="background: linear-gradient(135deg, #3B82F6, #2563EB);"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                {{ directT('settings.dealsSettings.existingCards.addNewCard') }}
              </button>
            </div>
            
            <div class="grid grid-cols-1 gap-4">
              <div v-for="(card, cardIndex) in defaultCards" :key="card.id" 
                   class="group bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200">
                
                <div class="flex items-start justify-between mb-3">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm" :class="card.iconBg">
                      <svg class="w-4 h-4" :class="card.iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="card.iconPath"></path>
                      </svg>
                    </div>
                    <div>
                      <h5 class="text-sm font-medium text-gray-900 mb-1">{{ card.title }}</h5>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {{ directT('settings.dealsSettings.existingCards.statusActive') }}
                    </span>
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {{ directT('settings.dealsSettings.existingCards.statusBuiltIn') }}
                    </span>
                    <button @click="editDefaultCard(cardIndex)" 
                            class="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-all duration-200 flex items-center justify-center">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div class="text-xs text-gray-600 mb-3">
                  {{ card.description }}
                </div>
                
                <!-- Fields List -->
                <div class="space-y-2">
                  <div class="text-xs font-medium text-gray-700 mb-1">{{ directT('settings.dealsSettings.existingCards.includedFields') }}</div>
                  <div class="flex flex-wrap gap-1">
                    <span v-for="field in card.fields" :key="field" 
                          class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      {{ field }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Custom Cards Section -->
          <div class="space-y-4">
            <div v-if="systemAreas.length === 0" class="text-center py-12">
              <div class="mx-auto w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.existingCards.noCardsTitle') }}</h4>
              <p class="text-gray-500 max-w-sm mx-auto">{{ directT('settings.dealsSettings.existingCards.noCardsDescription') }}</p>
            </div>
            
            <!-- Custom Cards Display -->
            <div v-else class="grid grid-cols-1 gap-4">
              <div v-for="(area, areaIndex) in systemAreas" :key="area.id" 
                   class="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200 shadow-sm">
                
                <!-- Card Header -->
                <div class="flex items-start justify-between mb-3">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shadow-sm">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900">{{ area.title }}</h3>
                      <p class="text-xs text-gray-500">{{ t('settings.dealsSettings.fieldsConfigured', { count: area.fields.length }) }}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {{ directT('settings.dealsSettings.existingCards.statusCustom') }}
                    </span>
                    <button @click="editArea(areaIndex)" 
                            class="w-8 h-8 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-all duration-200 flex items-center justify-center"
                            title="Edit Card">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    
                    <button @click="deleteArea(areaIndex)" 
                            class="w-8 h-8 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-all duration-200 flex items-center justify-center"
                            title="Delete Card">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Fields Preview -->
                <div class="space-y-2">
                  <div class="text-xs font-medium text-gray-700 mb-1">{{ directT('settings.dealsSettings.existingCards.fields') }}</div>
                  <div v-if="area.fields.length > 0" class="space-y-1">
                    <div v-for="(field, fieldIndex) in area.fields.slice(0, 3)" :key="field.id || fieldIndex" 
                         class="flex items-center gap-2 text-xs">
                      <div class="w-2 h-2 rounded-full bg-blue-400"></div>
                      <span class="text-gray-900 truncate flex-1 font-medium">{{ field.label }}</span>
                      <span class="text-gray-500 text-xs shrink-0 bg-gray-100 px-2 py-0.5 rounded">{{ getFieldTypeLabel(field.type) }}</span>
                    </div>
                    <div v-if="area.fields.length > 3" class="text-xs text-gray-500 ml-4">
                      {{ directT('settings.dealsSettings.existingCards.moreFields', { count: area.fields.length - 3 }) }}
                    </div>
                  </div>
                  
                                      <div v-else class="text-xs text-gray-500 italic bg-gray-50 p-2 rounded text-center">
                      {{ directT('settings.dealsSettings.existingCards.noFieldsConfigured') }}
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Important Notice -->
      <div class="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <div class="flex items-start gap-3">
          <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mt-0.5 flex-shrink-0">
            <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <div class="text-amber-800">
            <p class="font-semibold mb-2">{{ directT('settings.dealsSettings.importantNotice.title') }}</p>
            <p class="text-sm text-amber-700">{{ directT('settings.dealsSettings.importantNotice.description') }}</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Status Edit Modal -->
    <div v-if="showStatusModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="closeStatusModal"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">{{ editingStatusIndex === -1 ? directT('settings.dealsSettings.statusManagement.addStatusTitle') : directT('settings.dealsSettings.statusManagement.editStatusTitle') }}</h3>
                <p class="text-white/90 text-sm">{{ editingStatusIndex === -1 ? directT('settings.dealsSettings.statusManagement.addStatusDescription') : directT('settings.dealsSettings.statusManagement.editStatusDescription') }}</p>
              </div>
            </div>
            <button @click="closeStatusModal" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-gradient-to-br from-blue-100 to-sky-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
              </svg>
            </div>
            <h4 class="text-lg font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.statusManagement.configurationTitle') }}</h4>
            <p class="text-gray-600 text-sm">{{ directT('settings.dealsSettings.statusManagement.configurationDescription') }}</p>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.statusManagement.statusName') }}</label>
              <input
                v-model="editingStatus.name"
                type="text"
                class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
                :placeholder="directT('settings.dealsSettings.statusManagement.statusNamePlaceholder')"
              />
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.statusManagement.statusColor') }}</label>
              <div class="flex items-center gap-3">
                <input
                  v-model="editingStatus.color"
                  type="color"
                  class="w-16 h-12 rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300"
                />
                <div class="flex-1">
                  <div class="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div class="w-6 h-6 rounded-full border-2 border-white shadow-sm" :style="{ backgroundColor: editingStatus.color }"></div>
                    <div>
                      <p class="text-sm font-medium text-gray-900">{{ editingStatus.name || directT('settings.dealsSettings.statusManagement.statusPreview') }}</p>
                      <p class="text-xs text-gray-500">{{ editingStatus.color }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-500">
            <span class="font-medium">{{ directT('settings.dealsSettings.statusManagement.tip') }}</span> {{ directT('settings.dealsSettings.statusManagement.tipDescription') }}
          </div>
          <div class="flex gap-2">
            <button
              @click="closeStatusModal"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
            >
              {{ directT('settings.dealsSettings.statusManagement.cancel') }}
            </button>
            <button
              @click="saveStatus"
              :disabled="!editingStatus.name.trim()"
              class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
              style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);"
            >
              {{ editingStatusIndex === -1 ? directT('settings.dealsSettings.statusManagement.add') : directT('settings.dealsSettings.statusManagement.update') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="closeDeleteModal"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #DC2626 0%, #B91C1C 50%, #991B1B 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">{{ directT('settings.dealsSettings.statusManagement.confirmDeleteTitle') }}</h3>
                <p class="text-white/90 text-sm">{{ directT('settings.dealsSettings.statusManagement.confirmDeleteDescription') }}</p>
              </div>
            </div>
            <button @click="closeDeleteModal" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-gradient-to-br from-red-100 to-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <h4 class="text-lg font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.statusManagement.deleteStatusTitle') }}</h4>
            <p class="text-gray-600 text-sm">{{ directT('settings.dealsSettings.statusManagement.deleteConfirmation') }}</p>
          </div>

          <!-- Warning Box -->
          <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex items-start gap-3">
              <div class="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <div class="text-red-800">
                <p class="font-semibold text-sm mb-1">{{ directT('settings.dealsSettings.statusManagement.warningTitle') }}</p>
                <p class="text-sm text-red-700">{{ directT('settings.dealsSettings.statusManagement.warningDescription') }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-500">
            <span class="font-medium">{{ directT('settings.dealsSettings.statusManagement.noteTitle') }}</span> {{ directT('settings.dealsSettings.statusManagement.noteDescription') }}
          </div>
          <div class="flex gap-2">
            <button
              @click="closeDeleteModal"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
            >
              {{ directT('settings.dealsSettings.statusManagement.cancel') }}
            </button>
            <button
              @click="deleteStatus"
              class="px-6 py-2 text-white rounded-lg hover:shadow-lg transition-all duration-200 text-sm font-medium shadow-md"
              style="background: linear-gradient(135deg, #DC2626 0%, #B91C1C 50%, #991B1B 100%);"
            >
              {{ directT('settings.dealsSettings.statusManagement.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Toast Notification -->
    <div 
      v-if="toastMessage" 
      class="fixed bottom-4 right-4 max-w-sm rounded-xl shadow-2xl border backdrop-blur-md z-50 transition-all duration-300 transform hover:scale-105"
      :class="{ 
        'bg-emerald-50/95 text-emerald-700 border-emerald-200': toastType === 'success',
        'bg-red-50/95 text-red-700 border-red-200': toastType === 'error',
        'bg-blue-50/95 text-blue-700 border-blue-200': toastType === 'info'
      }"
    >
      <div class="p-4">
        <div class="flex items-start gap-3">
          <div 
            class="w-8 h-8 rounded-lg flex items-center justify-center"
            :class="{ 
              'bg-emerald-100': toastType === 'success',
              'bg-red-100': toastType === 'error',
              'bg-blue-100': toastType === 'info'
            }"
          >
            <svg v-if="toastType === 'success'" class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <svg v-else-if="toastType === 'error'" class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <svg v-else class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <span 
                class="text-sm font-semibold"
                :class="{ 
                  'text-emerald-900': toastType === 'success',
                  'text-red-900': toastType === 'error',
                  'text-blue-900': toastType === 'info'
                }"
              >
                {{ toastType === 'success' ? directT('settings.dealsSettings.toastMessages.successTitle') : toastType === 'error' ? directT('settings.dealsSettings.toastMessages.errorTitle') : directT('settings.dealsSettings.toastMessages.infoTitle') }}
              </span>
              <button 
                @click="closeToast" 
                class="w-6 h-6 rounded-lg hover:bg-black/10 transition-all duration-200 flex items-center justify-center"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <p class="text-sm mt-1">{{ toastMessage }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Card Creation Modal -->
    <div v-if="isAddingCard" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="cancelCardCreation"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 z-10 relative border border-gray-200 max-h-[80vh] overflow-hidden">
        
        <!-- Step 1: Card Name -->
        <div v-if="currentStep === 1" class="flex flex-col h-full">
          <!-- Header with gradient -->
          <div class="px-6 py-4" style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">{{ directT('settings.dealsSettings.cardCreation.createCustomCard') }}</h3>
                  <p class="text-white/90 text-sm">{{ directT('settings.dealsSettings.cardCreation.step1') }}</p>
                </div>
              </div>
              <button @click="cancelCardCreation" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 p-6">
            <div class="max-w-md mx-auto">
              <div class="text-center mb-6">
                <div class="w-16 h-16 bg-gradient-to-br from-blue-100 to-sky-100 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md">
                  <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <h4 class="text-lg font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.cardCreation.cardInformation') }}</h4>
                <p class="text-gray-600">{{ directT('settings.dealsSettings.cardCreation.cardInformationDescription') }}</p>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.cardCreation.cardName') }}</label>
                  <input
                    v-model="newArea.title"
                    type="text"
                    :placeholder="directT('settings.dealsSettings.cardCreation.cardNamePlaceholder')"
                    class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
                  />
                </div>
                
                <!-- Preview Card -->
                <div class="mt-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                      </svg>
                    </div>
                    <div>
                      <h5 class="text-sm font-medium text-gray-900">{{ newArea.title || directT('settings.dealsSettings.cardCreation.cardNamePreview') }}</h5>
                      <p class="text-xs text-gray-500">{{ directT('settings.dealsSettings.cardCreation.customCard') }}</p>
                    </div>
                  </div>
                  <p class="text-xs text-gray-500">{{ directT('settings.dealsSettings.cardCreation.cardPreviewDescription') }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div class="text-xs text-gray-500">
              <span class="font-medium">{{ directT('settings.dealsSettings.cardCreation.nextStep') }}</span> {{ directT('settings.dealsSettings.cardCreation.nextStepDescription') }}
            </div>
            <div class="flex gap-2">
              <button
                @click="cancelCardCreation"
                class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
              >
                {{ directT('settings.dealsSettings.cardCreation.cancel') }}
              </button>
              <button
                @click="proceedToFieldSelection"
                :disabled="!newArea.title.trim()"
                class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
                style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);"
              >
                {{ directT('settings.dealsSettings.cardCreation.continue') }}
              </button>
            </div>
          </div>
        </div>

                <!-- Step 2: Field Selection -->
        <div v-if="currentStep === 2" class="flex h-[80vh]">
          <!-- Left Side: Field Types -->
          <div class="w-1/2 border-r border-gray-200 flex flex-col bg-gray-50">
            <!-- Header -->
            <div class="px-4 py-3" style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-6 h-6 bg-white/30 rounded flex items-center justify-center border border-white/20">
                  <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </div>
                <h4 class="text-sm font-semibold text-white">{{ directT('settings.dealsSettings.cardCreation.addFields') }}</h4>
              </div>
              <p class="text-white/90 text-xs">{{ directT('settings.dealsSettings.cardCreation.addFieldsDescription') }}</p>
            </div>
            
            <div class="flex-1 p-3 overflow-y-auto">
              <div class="grid grid-cols-1 gap-2">
                <button
                  v-for="fieldType in fieldTypes"
                  :key="fieldType.id"
                  @click="selectFieldType(fieldType)"
                  class="group p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all duration-200"
                >
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 bg-blue-100 group-hover:bg-blue-200 rounded flex items-center justify-center transition-colors duration-200">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="fieldType.icon"></path>
                      </svg>
                    </div>
                    <div>
                      <span class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">{{ fieldType.name }}</span>
                      <p class="text-xs text-gray-500">{{ directT('settings.dealsSettings.cardCreation.addThisFieldType') }}</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Right Side: Card Preview -->
          <div class="w-1/2 flex flex-col bg-white">
            <!-- Header -->
            <div class="px-4 py-3" style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);">
              <div class="flex items-center justify-between">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <div class="w-6 h-6 bg-white/30 rounded flex items-center justify-center border border-white/20">
                      <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                      </svg>
                    </div>
                    <h4 class="text-sm font-semibold text-white">{{ newArea.title }}</h4>
                  </div>
                  <p class="text-white/90 text-xs">{{ t('settings.dealsSettings.fieldsConfigured', { count: newArea.fields.length }) }}</p>
                </div>
                <button
                  @click="finishCardCreation"
                  :disabled="newArea.fields.length === 0"
                  class="px-3 py-1.5 text-xs font-medium text-blue-600 bg-white rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm disabled:shadow-none"
                >
                  {{ directT('settings.dealsSettings.cardCreation.saveCard') }}
                </button>
              </div>
            </div>
            
            <div class="flex-1 p-3 overflow-y-auto">
              <div v-if="newArea.fields.length === 0" class="text-center py-8">
                <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </div>
                <h5 class="text-sm font-medium text-gray-900 mb-1">{{ directT('settings.dealsSettings.cardCreation.noFieldsYet') }}</h5>
                <p class="text-xs text-gray-500">{{ directT('settings.dealsSettings.cardCreation.noFieldsYetDescription') }}</p>
              </div>
              
              <div v-else class="space-y-2">
                <div
                  v-for="(field, index) in newArea.fields"
                  :key="field.id || index"
                  class="group p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                >
                  <div class="flex items-start justify-between">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <div class="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                          <svg class="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center gap-2">
                            <span class="text-sm font-medium text-gray-900">{{ field.label }}</span>
                            <span v-if="field.required" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{{ directT('settings.dealsSettings.cardCreation.required') }}</span>
                          </div>
                          <div class="flex items-center gap-2 mt-0.5">
                            <span class="text-xs text-gray-600">{{ getFieldTypeLabel(field.type) }}</span>
                            <span class="text-gray-400">•</span>
                            <span class="text-xs text-gray-500 truncate">{{ field.placeholder }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        @click="editFieldInCard(index)"
                        class="w-6 h-6 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded flex items-center justify-center transition-all duration-200"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      <button
                        @click="removeFieldFromCard(index)"
                        class="w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded flex items-center justify-center transition-all duration-200"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Footer -->
            <div class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <button
                @click="currentStep = 1"
                class="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors duration-200 text-sm"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                {{ directT('settings.dealsSettings.cardCreation.backToCardInfo') }}
              </button>
              <div class="text-xs text-gray-500">
                {{ t('settings.dealsSettings.fieldsConfigured', { count: newArea.fields.length }) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Field Configuration Modal -->
    <div v-if="showCustomFieldModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="closeCustomFieldModal"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #A855F7 0%, #9333EA 50%, #7C3AED 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">{{ directT('settings.dealsSettings.fieldConfiguration.title') }}</h3>
                <p class="text-white/90 text-sm">{{ directT('settings.dealsSettings.fieldConfiguration.description') }}</p>
              </div>
            </div>
            <button @click="closeCustomFieldModal" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <!-- Field Label -->
          <div>
            <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.fieldConfiguration.fieldLabel') }}</label>
            <input
              v-model="newField.label"
              type="text"
              :placeholder="directT('settings.dealsSettings.fieldConfiguration.fieldLabelPlaceholder')"
              class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-100 focus:border-purple-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
            />
          </div>

          <!-- Field Type -->
          <div>
            <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.fieldConfiguration.fieldType') }}</label>
            <select
              v-model="newField.type"
              class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-100 focus:border-purple-500 bg-gray-50 text-gray-900 transition-all duration-300"
            >
              <option value="" disabled>{{ directT('settings.dealsSettings.fieldConfiguration.selectFieldType') }}</option>
              <option v-for="type in fieldTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
            </select>
          </div>

          <!-- Placeholder -->
          <div>
            <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.dealsSettings.fieldConfiguration.placeholderText') }}</label>
            <input
              v-model="newField.placeholder"
              type="text"
              :placeholder="directT('settings.dealsSettings.fieldConfiguration.placeholderTextPlaceholder')"
              class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-100 focus:border-purple-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
            />
          </div>

          <!-- Required Field Toggle -->
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label class="text-sm font-semibold text-gray-900">{{ directT('settings.dealsSettings.fieldConfiguration.requiredField') }}</label>
              <p class="text-xs text-gray-500">{{ directT('settings.dealsSettings.fieldConfiguration.requiredFieldDescription') }}</p>
            </div>
            <button
              @click="newField.required = !newField.required"
              :class="[
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
                newField.required ? 'bg-red-600' : 'bg-gray-300'
              ]"
            >
              <span
                :class="[
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  newField.required ? 'translate-x-5' : 'translate-x-0'
                ]"
              />
            </button>
          </div>

          <!-- Options for select/multiselect -->
          <div v-if="newField.type === 'select' || newField.type === 'multiselect'" class="space-y-3">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4"></path>
                </svg>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-900">{{ directT('settings.dealsSettings.fieldConfiguration.options') }}</label>
                <p class="text-xs text-gray-500">{{ directT('settings.dealsSettings.fieldConfiguration.optionsDescription') }}</p>
              </div>
            </div>
            
            <div class="space-y-2">
              <div v-for="(option, index) in newField.options" :key="index" class="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200">
                <div class="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-600">
                  {{ index + 1 }}
                </div>
                <input
                  v-model="newField.options[index]"
                  type="text"
                  :placeholder="directT('settings.dealsSettings.fieldConfiguration.optionText')"
                  class="flex-1 px-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-purple-100 focus:border-purple-500 bg-gray-50 text-gray-900 transition-all duration-300 text-sm"
                />
                <button
                  @click="removeOption(index)"
                  class="w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded flex items-center justify-center transition-all duration-200"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
              
              <button
                @click="addOptionToField"
                class="w-full px-3 py-2 text-purple-600 bg-purple-50 border border-dashed border-purple-200 rounded-lg hover:border-purple-300 hover:bg-purple-100 transition-all duration-200 text-sm font-medium"
              >
                <div class="flex items-center justify-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  {{ directT('settings.dealsSettings.fieldConfiguration.addOption') }}
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-500">
            {{ directT('settings.dealsSettings.fieldConfiguration.configureSettings') }}
          </div>
          <div class="flex gap-2">
            <button
              @click="closeCustomFieldModal"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
            >
              {{ directT('settings.dealsSettings.fieldConfiguration.cancel') }}
            </button>
            <button
              @click="addFieldToCard"
              :disabled="!newField.label.trim() || !newField.type"
              class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
              style="background: linear-gradient(135deg, #A855F7 0%, #9333EA 50%, #7C3AED 100%);"
            >
              {{ directT('settings.dealsSettings.fieldConfiguration.addField') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import SettingsNav from "../SettingsNav.vue";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface CustomerStatusRecord {
  id: string;
  statusname: string;
  color: string;
  organizationid: string;
  created_at?: string;
  updated_at?: string;
}

interface Status {
  id?: string;
  name: string;
  color: string;
  organizationid?: string;
}

interface CustomField {
  id: number | null;
  label: string;
  type: string;
  placeholder: string;
  required: boolean;
  options: string[];
  defaultValue: string;
}

interface CustomArea {
  id?: number;
  title: string;
  fields: CustomField[];
}

interface FieldType {
  id: string;
  name: string;
  icon: string;
  placeholder: string;
}

interface DatabaseCard {
  id: number;
  area_title: string;
  fields: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | string[] | TranslationObject;
};

export default defineComponent({
  name: "DealsSettings",
  components: {
    SettingsNav,
  },
  setup() {
    const statusList = ref<Status[]>([]);
    const saving = ref(false);
    const organizationId = ref<string | null>(null);
    
    // Status modal
    const showStatusModal = ref(false);
    const editingStatus = ref<Status>({ name: '', color: '#059669' });
    const editingStatusIndex = ref(-1);
    
    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleteIndex = ref<number>(-1);
    
    // Toast notification system
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<number | null>(null);
    
    // Toast functions
    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      // Clear existing timeout
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      // Auto-hide the toast after 3 seconds
      toastTimeout.value = window.setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    // Close the toast
    const closeToast = () => {
      toastMessage.value = '';
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    // i18n setup
    const { locale, t } = useI18n();
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject | string | string[] = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || typeof result === 'string' || Array.isArray(result) || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject | string | string[] = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || typeof enResult === 'string' || Array.isArray(enResult) || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject | string | string[];
          }
          return typeof enResult === 'string' ? enResult : Array.isArray(enResult) ? enResult.join(', ') : key;
        }
        result = result[part as keyof typeof result] as TranslationObject | string | string[];
      }
      
      if (typeof result === 'string') {
        return result;
      } else if (Array.isArray(result)) {
        return result.join(', '); // Join array elements with comma
      } else {
        return key; // Return the key if result is not a string or array
      }
    };
    
    // Fetch settings on component mount
    onMounted(async () => {
      try {
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          createToast(directT('settings.dealsSettings.toastMessages.missingOrg'), 'error');
          return;
        }
        
        await Promise.all([
          fetchDealSettings(),
          fetchCustomCards()
        ]);
      } catch (error) {
        console.error('Error loading deal settings:', error);
        createToast(directT('settings.dealsSettings.toastMessages.loadingError'), 'error');
      }
    });
    
    // Fetch deal settings from the database
    const fetchDealSettings = async () => {
      try {
        // Fetch deal statuses from customdealsstatus table
        const { data: statusData, error: statusError } = await supabase
          .from('customdealsstatus')
          .select('*')
          .eq('organizationid', organizationId.value as string);
        
        if (statusError) throw statusError;
        
        // Map the database records to the Status interface
        statusList.value = (statusData || []).map((item: any) => ({
          id: item.id as string,
          name: item.status as string,
          color: (item.color as string) || '#059669', // Default green color for deals
          organizationid: item.organizationid as string
        }));
        
        // If no statuses exist, create default ones
        if (statusList.value.length === 0) {
          await createDefaultStatuses();
        }
      } catch (error) {
        console.error('Error fetching deal settings:', error);
        createToast(directT('settings.dealsSettings.toastMessages.fetchError'), 'error');
      }
    };
    
    // Create default statuses if none exist
    const createDefaultStatuses = async () => {
      try {
        const defaultStatuses: Status[] = [
          { name: 'New Deal', color: '#059669', organizationid: organizationId.value as string },
          { name: 'In Progress', color: '#0891B2', organizationid: organizationId.value as string },
          { name: 'Negotiation', color: '#EA580C', organizationid: organizationId.value as string },
          { name: 'Closed Won', color: '#16A34A', organizationid: organizationId.value as string },
          { name: 'Closed Lost', color: '#DC2626', organizationid: organizationId.value as string },
        ];
        
        // Add default statuses to the database
        const { error } = await supabase
          .from('customdealsstatus')
          .insert(defaultStatuses.map(status => ({
            status: status.name,
            color: status.color,
            organizationid: status.organizationid
          })));
        
        if (error) throw error;
        
        // Refresh the status list
        await fetchDealSettings();
      } catch (error) {
        console.error('Error creating default statuses:', error);
        createToast(directT('settings.dealsSettings.toastMessages.defaultError'), 'error');
      }
    };
    
    // Open the status modal for adding a new status
    const addNewStatus = () => {
      editingStatus.value = { name: '', color: '#059669' };
      editingStatusIndex.value = -1;
      showStatusModal.value = true;
    };
    
    // Open the status modal for editing an existing status
    const editStatus = (index: number) => {
      const status = statusList.value[index];
      editingStatus.value = { ...status };
      editingStatusIndex.value = index;
      showStatusModal.value = true;
    };
    
    // Save the current status being edited
    const saveStatus = async () => {
      // Validate inputs
      if (!editingStatus.value.name.trim()) {
        createToast('Please enter a status name', 'error');
        return;
      }
      
      saving.value = true;
      
      try {
      if (editingStatusIndex.value === -1) {
          // Add new status directly to database
          const { data, error } = await supabase
            .from('customdealsstatus')
            .insert([{
              status: editingStatus.value.name,
              color: editingStatus.value.color,
              organizationid: organizationId.value as string
            }])
            .select()
            .single();
          
          if (error) throw error;
          
          // Add to local list with real ID
          statusList.value.push({
            id: data.id as string,
            name: data.status as string,
            color: data.color as string,
            organizationid: data.organizationid as string
          });
          
          createToast('Status added successfully!', 'success');
      } else {
          // Update existing status directly in database
          const statusToUpdate = statusList.value[editingStatusIndex.value];
          
          if (!statusToUpdate.id) {
            throw new Error('Status ID is missing');
          }
          
          const { error } = await supabase
            .from('customdealsstatus')
            .update({
              status: editingStatus.value.name,
              color: editingStatus.value.color
            })
            .eq('id', statusToUpdate.id);
          
          if (error) throw error;
          
          // Update local list
          statusList.value[editingStatusIndex.value] = {
            ...statusToUpdate,
            name: editingStatus.value.name,
            color: editingStatus.value.color
          };
          
          createToast('Status updated successfully!', 'success');
      }
      
      // Close the modal
      closeStatusModal();
      } catch (error: any) {
        console.error('Error saving status:', error);
        createToast(`Failed to save status: ${error.message || error}`, 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Close the status modal
    const closeStatusModal = () => {
      showStatusModal.value = false;
      editingStatus.value = { name: '', color: '#059669' };
      editingStatusIndex.value = -1;
    };
    
    // Confirm deletion of a status
    const confirmDeleteStatus = (index: number) => {
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    // Delete a status
    const deleteStatus = async () => {
      if (deleteIndex.value < 0 || deleteIndex.value >= statusList.value.length) {
      closeDeleteModal();
        return;
      }
      
      const statusToDelete = statusList.value[deleteIndex.value];
      
      if (!statusToDelete.id) {
        // If no ID, just remove from local list
        statusList.value.splice(deleteIndex.value, 1);
        closeDeleteModal();
        return;
      }
      
      saving.value = true;
      
      try {
        const { error } = await supabase
            .from('customdealsstatus')
            .delete()
          .eq('id', statusToDelete.id);
        
        if (error) throw error;
        
        // Remove from local list
        statusList.value.splice(deleteIndex.value, 1);
        createToast('Status deleted successfully!', 'success');
        
      } catch (error: any) {
        console.error('Error deleting status:', error);
        createToast(`Failed to delete status: ${error.message || error}`, 'error');
      } finally {
        saving.value = false;
      }
      
      closeDeleteModal();
    };
    
    // Close the delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deleteIndex.value = -1;
    };
    

    

    
    // Customer Form Designer variables
    const defaultCards = ref([
      {
        id: 1,
        title: 'Contact Information',
        iconBg: 'bg-blue-100 dark:bg-blue-900/30',
        iconColor: 'text-blue-600 dark:text-blue-400',
        iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
        description: 'Customer basic details like name, email, phone, company and status',
        fields: ['First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Status']
      }
    ]);

    const systemAreas = ref<CustomArea[]>([]);
    const newArea = ref<CustomArea>({ title: '', fields: [] });
    const isAddingCard = ref(false);
    const showCustomFieldModal = ref(false);
    const editingAreaIndex = ref(-1);
    const editingFieldIndex = ref(-1);
    const selectedFieldType = ref('');
    const currentStep = ref(1);

    const newField = ref<CustomField>({
      id: null,
      label: '',
      type: '',
      placeholder: '',
      required: false,
      options: [],
      defaultValue: ''
    });

    const fieldTypes = computed(() => [
      { id: 'text', name: directT('settings.dealsSettings.fieldTypes.text'), icon: 'M4 6h16M4 12h16m-7 6h7', placeholder: 'Enter text...' },
      { id: 'textarea', name: directT('settings.dealsSettings.fieldTypes.longText'), icon: 'M4 6h16M4 10h16M4 14h16M4 18h16', placeholder: 'Enter detailed text...' },
      { id: 'number', name: directT('settings.dealsSettings.fieldTypes.number'), icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14', placeholder: 'Enter number...' },
      { id: 'email', name: directT('settings.dealsSettings.fieldTypes.email'), icon: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', placeholder: 'example@email.com' },
      { id: 'phone', name: directT('settings.dealsSettings.fieldTypes.phone'), icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', placeholder: '+1 (555) 123-4567' },
      { id: 'url', name: directT('settings.dealsSettings.fieldTypes.url'), icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1', placeholder: 'https://example.com' },
      { id: 'date', name: directT('settings.dealsSettings.fieldTypes.date'), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', placeholder: 'Select date...' },
      { id: 'datetime', name: directT('settings.dealsSettings.fieldTypes.datetime'), icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', placeholder: 'Select date and time...' },
      { id: 'select', name: directT('settings.dealsSettings.fieldTypes.select'), icon: 'M8 9l4-4 4 4m0 6l-4 4-4-4', placeholder: 'Select option...' },
      { id: 'multiselect', name: directT('settings.dealsSettings.fieldTypes.multiselect'), icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', placeholder: 'Select options...' },
      { id: 'checkbox', name: directT('settings.dealsSettings.fieldTypes.checkbox'), icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', placeholder: 'Check if applicable' },
      { id: 'file', name: directT('settings.dealsSettings.fieldTypes.file'), icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12', placeholder: 'Upload file...' },
      { id: 'color', name: directT('settings.dealsSettings.fieldTypes.color'), icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 3H5v12a2 2 0 104 0V3z', placeholder: 'Select color...' },
      { id: 'user', name: directT('settings.dealsSettings.fieldTypes.user'), icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', placeholder: 'Select user...' }
    ]);

    // Customer Form Designer functions
    const addNewCard = () => {
      newArea.value = { title: '', fields: [] };
      editingAreaIndex.value = -1;
      currentStep.value = 1;
      isAddingCard.value = true;
    };

    const editDefaultCard = (cardIndex: number) => {
      const defaultCard = defaultCards.value[cardIndex];
      
      // Create a new custom card based on the default card
      const customCard = {
        id: Date.now(),
        title: defaultCard.title,
        fields: [] // Start with empty fields for user to configure
      };
      
      // Add to custom cards
      systemAreas.value.push(customCard);
      
      // Edit the newly created card
      const newIndex = systemAreas.value.length - 1;
      editArea(newIndex);
      
      createToast('Default card converted to custom card for editing', 'info');
    };



    const editArea = (index: number) => {
      const area = systemAreas.value[index];
      newArea.value = { ...area, fields: [...area.fields] };
      editingAreaIndex.value = index;
      
      // Reset field editing state
      editingFieldIndex.value = -1;
      selectedFieldType.value = '';
      newField.value = {
        id: null,
        label: '',
        type: '',
        placeholder: '',
        required: false,
        options: [],
        defaultValue: ''
      };
      
      // Go directly to step 2 to show the existing fields
      currentStep.value = 2;
      isAddingCard.value = true;
    };

    const deleteArea = async (index: number) => {
      const area = systemAreas.value[index];
      
      try {
        // Find and delete the area from database (same as leads)
        const { data: existingData, error: queryError } = await supabase
          .from('customdealsfield')
          .select('id, content')
          .eq('organizationid', organizationId.value as string);

        if (queryError) throw queryError;
        
        // Find existing area manually since LIKE with JSON doesn't work reliably
        const existingArea = existingData?.find(item => {
          if (item.content && Array.isArray(item.content)) {
            const content = item.content as any[];
            return content.length > 0 && content[0].areaId === area.id;
          }
          return false;
        });

        if (existingArea) {
          // Delete from database
          const { error: deleteError } = await supabase
            .from('customdealsfield')
            .delete()
            .eq('id', existingArea.id);
          
          if (deleteError) throw deleteError;
          console.log('Deleted area from database:', area.title);
        }
        
        // Remove from local array only after successful database deletion
        systemAreas.value.splice(index, 1);
        createToast('Card deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting area:', error);
        createToast('Failed to delete card', 'error');
      }
    };

    const proceedToFieldSelection = () => {
      if (!newArea.value.title.trim()) {
        createToast('Please enter a card name', 'error');
        return;
      }
      currentStep.value = 2;
    };

    const cancelCardCreation = () => {
      isAddingCard.value = false;
      newArea.value = { title: '', fields: [] };
      editingAreaIndex.value = -1;
      currentStep.value = 1;
    };

    const selectFieldType = (fieldType: FieldType) => {
      selectedFieldType.value = fieldType.id;
      newField.value = {
        id: Date.now(),
        label: '',
        type: fieldType.id,
        placeholder: fieldType.placeholder,
        required: false,
        options: fieldType.id === 'select' || fieldType.id === 'multiselect' ? [''] : [],
        defaultValue: ''
      };
      showCustomFieldModal.value = true;
    };

    const closeCustomFieldModal = () => {
      showCustomFieldModal.value = false;
      selectedFieldType.value = '';
      newField.value = {
        id: null,
        label: '',
        type: '',
        placeholder: '',
        required: false,
        options: [],
        defaultValue: ''
      };
    };

    const addFieldToCard = () => {
      if (!newField.value.label.trim()) {
        createToast('Please enter a field label', 'error');
        return;
      }
      
      if (editingFieldIndex.value >= 0) {
        newArea.value.fields[editingFieldIndex.value] = { ...newField.value };
        editingFieldIndex.value = -1;
      } else {
        newArea.value.fields.push({ ...newField.value });
      }
      
      closeCustomFieldModal();
    };

    const removeFieldFromCard = (fieldIndex: number) => {
      newArea.value.fields.splice(fieldIndex, 1);
    };

    const editFieldInCard = (fieldIndex: number) => {
      const field = newArea.value.fields[fieldIndex];
      newField.value = { ...field };
      editingFieldIndex.value = fieldIndex;
      selectedFieldType.value = field.type;
      showCustomFieldModal.value = true;
    };

    const finishCardCreation = async () => {
      if (!newArea.value.title.trim()) {
        createToast('Please enter a card name', 'error');
        return;
      }
      
      if (newArea.value.fields.length === 0) {
        createToast('Please add at least one field to the card', 'error');
        return;
      }
      
      try {
        // Convert area to the format expected by customdealsfield (same as leads)
        const fieldsToSave = newArea.value.fields.map(field => ({
          key: `${newArea.value.title}_${field.id}`,
          label: `${newArea.value.title} - ${field.label}`,
          type: field.type,
          required: field.required,
          area: newArea.value.title,
          areaId: editingAreaIndex.value >= 0 ? systemAreas.value[editingAreaIndex.value].id?.toString() : Date.now().toString()
        }));

        if (editingAreaIndex.value >= 0) {
          // Update existing card
          const existingCard = systemAreas.value[editingAreaIndex.value];
          const { error: updateError } = await supabase
            .from('customdealsfield')
            .update({ 
              content: fieldsToSave,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingCard.id);
          
          if (updateError) throw updateError;
          
          systemAreas.value[editingAreaIndex.value] = {
            id: existingCard.id,
            title: newArea.value.title,
            fields: [...newArea.value.fields]
          };
          
          createToast('Card updated successfully', 'success');
        } else {
          // Create new card - insert as separate record
          const { data, error: insertError } = await supabase
            .from('customdealsfield')
            .insert({
              organizationid: organizationId.value as string,
              content: fieldsToSave,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select();
          
          if (insertError) throw insertError;
          
          if (data && data.length > 0) {
            systemAreas.value.push({
              id: (data as any[])[0].id,
              title: newArea.value.title,
              fields: [...newArea.value.fields]
            });
          }
          
          createToast('Card created successfully', 'success');
        }
        
        cancelCardCreation();
      } catch (error) {
        console.error('Error saving card:', error);
        createToast('Failed to save card', 'error');
      }
    };

    const addOptionToField = () => {
      newField.value.options.push('');
    };

    const removeOption = (index: number) => {
      newField.value.options.splice(index, 1);
    };

    const getFieldTypeLabel = (type: string) => {
      const fieldType = fieldTypes.value.find((ft: FieldType) => ft.id === type);
      return fieldType ? fieldType.name : type;
    };

    // Load custom cards on mount
    const fetchCustomCards = async () => {
      try {
              const { data, error } = await supabase
        .from('customdealsfield')
        .select('id, content')
        .eq('organizationid', organizationId.value as string);
        
        if (error) throw error;
        
        // Filter and load system areas (same as leads)
        const loadedAreas: any[] = [];
        
        if (data) {
          for (const record of data) {
            if (record.content && Array.isArray(record.content)) {
              const content = record.content as any[];
              
              // Check if this is a system area (has areaId)
              if (content.length > 0 && content[0].areaId) {
                const areaId = content[0].areaId;
                const areaTitle = content[0].area;
                
                // Check if we already have this area
                let existingArea = loadedAreas.find(area => area.id === areaId);
                
                if (!existingArea) {
                  existingArea = {
                    id: areaId,
                    title: areaTitle,
                    fields: []
                  };
                  loadedAreas.push(existingArea);
                }
                
                // Add fields to the area
                for (const fieldData of content) {
                  const field: any = {
                    id: fieldData.key.split('_')[1] || fieldData.key,
                    label: fieldData.label.replace(`${areaTitle} - `, ''),
                    type: fieldData.type,
                    required: fieldData.required || false
                  };
                  existingArea.fields.push(field);
                }
              }
            }
          }
        }

        systemAreas.value = loadedAreas;
        console.log('System areas loaded:', systemAreas.value);
      } catch (error) {
        console.error('Error fetching custom cards:', error);
      }
    };


    
    return {
      statusList,
      saving,
      organizationId,
      showStatusModal,
      editingStatus,
      editingStatusIndex,
      showDeleteModal,
      deleteIndex,

      directT,
      t,
      addNewStatus,
      editStatus,
      saveStatus,
      closeStatusModal,
      confirmDeleteStatus,
      deleteStatus,
      closeDeleteModal,
      // Customer Form Designer
      defaultCards,
      systemAreas,
      newArea,
      isAddingCard,
      showCustomFieldModal,
      editingAreaIndex,
      editingFieldIndex,
      selectedFieldType,
      currentStep,
      newField,
      fieldTypes,
      addNewCard,
      editDefaultCard,
      editArea,
      deleteArea,
      proceedToFieldSelection,
      cancelCardCreation,
      selectFieldType,
      closeCustomFieldModal,
      addFieldToCard,
      removeFieldFromCard,
      editFieldInCard,
      finishCardCreation,
      addOptionToField,
      removeOption,
      getFieldTypeLabel,
      toastMessage,
      toastType,
      toastTimeout,
      showToast,
      closeToast
    };
  }
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #0c1427;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Deals Settings Dark Mode Support */

/* Main container and backgrounds */
.dark .min-h-screen {
  background-color: #0f172a !important;
}

.dark .bg-gray-50 {
  background-color: #0f172a !important;
}

.dark .bg-white {
  background-color: #1e293b !important;
}

.dark .bg-gray-100 {
  background-color: #334155 !important;
}

.dark .bg-gray-200 {
  background-color: #475569 !important;
}

/* Text colors */
.dark .text-gray-900 {
  color: #f1f5f9 !important;
}

.dark .text-gray-700 {
  color: #cbd5e1 !important;
}

.dark .text-gray-600 {
  color: #94a3b8 !important;
}

.dark .text-gray-500 {
  color: #64748b !important;
}

.dark .text-gray-400 {
  color: #475569 !important;
}

/* Borders */
.dark .border-gray-200 {
  border-color: #475569 !important;
}

.dark .border-gray-300 {
  border-color: #64748b !important;
}

.dark .border-t {
  border-color: #475569 !important;
}

.dark .border-r {
  border-color: #475569 !important;
}

/* Form elements */
.dark input[type="text"],
.dark input[type="email"],
.dark input[type="number"],
.dark input[type="tel"],
.dark input[type="url"],
.dark input[type="color"],
.dark textarea,
.dark select {
  background-color: #475569 !important;
  border-color: #64748b !important;
  color: #f1f5f9 !important;
}

.dark input[type="text"]:focus,
.dark input[type="email"]:focus,
.dark input[type="number"]:focus,
.dark input[type="tel"]:focus,
.dark input[type="url"]:focus,
.dark textarea:focus,
.dark select:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

.dark input::placeholder,
.dark textarea::placeholder {
  color: #94a3b8 !important;
}

/* Hover states */
.dark .hover\:bg-gray-50:hover {
  background-color: #334155 !important;
}

.dark .hover\:bg-gray-100:hover {
  background-color: #475569 !important;
}

.dark .hover\:bg-gray-200:hover {
  background-color: #64748b !important;
}

.dark .hover\:border-gray-300:hover {
  border-color: #64748b !important;
}

.dark .hover\:text-gray-600:hover {
  color: #cbd5e1 !important;
}

.dark .hover\:text-gray-800:hover {
  color: #f1f5f9 !important;
}

/* Status management cards */
.dark .group:hover .bg-gray-50 {
  background-color: #334155 !important;
}

.dark .group:hover .bg-gray-100 {
  background-color: #475569 !important;
}

/* Custom Cards Section */
.dark .bg-slate-50 {
  background-color: #1e293b !important;
}

.dark .bg-slate-100 {
  background-color: #334155 !important;
}

.dark .border-slate-200 {
  border-color: #475569 !important;
}

.dark .border-slate-300 {
  border-color: #64748b !important;
}

.dark .text-slate-900 {
  color: #f1f5f9 !important;
}

.dark .text-slate-600 {
  color: #cbd5e1 !important;
}

.dark .text-slate-500 {
  color: #94a3b8 !important;
}

.dark .text-slate-400 {
  color: #64748b !important;
}

.dark .hover\:border-slate-300:hover {
  border-color: #64748b !important;
}

.dark .hover\:border-slate-500:hover {
  border-color: #94a3b8 !important;
}

.dark .hover\:text-slate-600:hover {
  color: #cbd5e1 !important;
}

/* Modals */
.dark .fixed.inset-0 .bg-white {
  background-color: #1e293b !important;
}

.dark .fixed.inset-0 .border-gray-200 {
  border-color: #475569 !important;
}

/* Modal backgrounds */
.dark .fixed.inset-0 .bg-gray-50 {
  background-color: #0f172a !important;
}

/* Status and field type indicators */
.dark .bg-blue-100 {
  background-color: #1e3a8a !important;
}

.dark .bg-red-100 {
  background-color: #7f1d1d !important;
}

.dark .bg-green-100 {
  background-color: #14532d !important;
}

.dark .bg-yellow-100 {
  background-color: #713f12 !important;
}

.dark .bg-purple-100 {
  background-color: #581c87 !important;
}

.dark .text-blue-600 {
  color: #60a5fa !important;
}

.dark .text-red-600 {
  color: #f87171 !important;
}

.dark .text-green-600 {
  color: #4ade80 !important;
}

.dark .text-yellow-600 {
  color: #facc15 !important;
}

.dark .text-purple-600 {
  color: #c084fc !important;
}

/* Hover states for colored backgrounds */
.dark .hover\:bg-blue-200:hover {
  background-color: #1e40af !important;
}

.dark .hover\:bg-red-200:hover {
  background-color: #991b1b !important;
}

.dark .hover\:bg-green-200:hover {
  background-color: #166534 !important;
}

.dark .hover\:bg-yellow-200:hover {
  background-color: #92400e !important;
}

.dark .hover\:bg-purple-200:hover {
  background-color: #6b21a8 !important;
}

/* Field type selection modal */
.dark .group .bg-white {
  background-color: #334155 !important;
}

.dark .group:hover .bg-white {
  background-color: #475569 !important;
}

.dark .group:hover .text-blue-600 {
  color: #60a5fa !important;
}

/* Card creation modals */
.dark .bg-gradient-to-br {
  background: #334155 !important;
}

.dark .bg-gradient-to-br.from-gray-50 {
  background: #1e293b !important;
}

.dark .bg-gradient-to-br.from-blue-100 {
  background: #1e3a8a !important;
}

.dark .bg-gradient-to-br.from-red-100 {
  background: #7f1d1d !important;
}

.dark .bg-gradient-to-br.from-purple-100 {
  background: #581c87 !important;
}

/* Toggle switches */
.dark .bg-gray-300 {
  background-color: #64748b !important;
}

/* Preview cards */
.dark .bg-gray-50.rounded-lg {
  background-color: #334155 !important;
}

/* Amber/warning sections */
.dark .bg-amber-50 {
  background-color: #451a03 !important;
}

.dark .border-amber-200 {
  border-color: #92400e !important;
}

.dark .text-amber-800 {
  color: #fbbf24 !important;
}

.dark .text-amber-700 {
  color: #f59e0b !important;
}

.dark .bg-amber-100 {
  background-color: #713f12 !important;
}

.dark .text-amber-600 {
  color: #fbbf24 !important;
}

/* Toast notifications */
.dark .bg-emerald-50\/95 {
  background-color: rgba(6, 78, 59, 0.95) !important;
}

.dark .text-emerald-700 {
  color: #34d399 !important;
}

.dark .border-emerald-200 {
  border-color: #065f46 !important;
}

.dark .bg-emerald-100 {
  background-color: #064e3b !important;
}

.dark .text-emerald-600 {
  color: #10b981 !important;
}

.dark .text-emerald-900 {
  color: #6ee7b7 !important;
}

.dark .bg-red-50\/95 {
  background-color: rgba(127, 29, 29, 0.95) !important;
}

.dark .text-red-700 {
  color: #f87171 !important;
}

.dark .border-red-200 {
  border-color: #991b1b !important;
}

.dark .text-red-900 {
  color: #fca5a5 !important;
}

.dark .bg-blue-50\/95 {
  background-color: rgba(30, 58, 138, 0.95) !important;
}

.dark .text-blue-700 {
  color: #60a5fa !important;
}

.dark .border-blue-200 {
  border-color: #1e40af !important;
}

.dark .text-blue-900 {
  color: #93c5fd !important;
}

/* Disabled states */
.dark button:disabled {
  background-color: #374151 !important;
  color: #6b7280 !important;
}

/* Shadow adjustments */
.dark .shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3) !important;
}

.dark .shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
}

.dark .hover\:shadow-sm:hover {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3) !important;
}

.dark .hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.dark .hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2) !important;
}

.dark .hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

/* Custom field type colors in dark mode */
.dark .bg-teal-100 {
  background-color: #134e4a !important;
}

.dark .text-teal-600 {
  color: #5eead4 !important;
}

.dark .bg-emerald-100 {
  background-color: #064e3b !important;
}

.dark .text-emerald-600 {
  color: #6ee7b7 !important;
}

.dark .bg-sky-100 {
  background-color: #0c4a6e !important;
}

.dark .text-sky-600 {
  color: #7dd3fc !important;
}

.dark .bg-cyan-100 {
  background-color: #164e63 !important;
}

.dark .text-cyan-600 {
  color: #67e8f9 !important;
}

.dark .bg-violet-100 {
  background-color: #4c1d95 !important;
}

.dark .text-violet-600 {
  color: #c4b5fd !important;
}

.dark .bg-amber-100 {
  background-color: #713f12 !important;
}

.dark .text-amber-600 {
  color: #fbbf24 !important;
}

.dark .bg-orange-100 {
  background-color: #7c2d12 !important;
}

.dark .text-orange-600 {
  color: #fb923c !important;
}

/* Keep gradient buttons with their original colors */
</style> 
